import { useState, FormEvent, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import Select from "react-select";
import { addTemplate } from "api/template";
// import { addTemplate, updateTemplate } from "api/template";

type FormErrors = Record<string, boolean>;

type FormRecords = {
  name: string;
  type: string;
  id_positions: number[];
  id_executors: number[];
  id_locations: number[];
  todo_items: string[];
  [key: string]: string | string[] | number[];
};

type SelectOption = {
  value: number;
  label: string;
};

const REQUIRED_FIELDS = ["name", "type", "id_positions", "id_locations"] as const;

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте договор" : "Введите корректное значение";

const TYPE_OPTIONS = [
  {
    value: 1,
    label: "Оплаты",
  },
  {
    value: 2,
    label: "Договоры",
  },
];

export default function NewTemplate() {
  const { positions, getPositionsData, locations, getLocationsData, templates, getTemplatesData, notify } =
    useSharedState();
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [formRecords, setFormRecords] = useState<FormRecords>({
    name: "",
    type: "",
    id_positions: [],
    id_executors: [],
    id_locations: [],
    todo_items: [],
  });

  const isCreatorAnExecutor = useMemo(() => {
    return !!formRecords?.id_executors?.find(
      (id) => id === positions.find((pos) => pos.position === "Создатель")?.position_id
    );
  }, [formRecords]);

  useEffect(() => {
    getPositionsData();
    getLocationsData();
  }, []);

  useEffect(() => {
    if (templateId) {
      const template = templates.find((t) => t.template_id === Number(templateId));
      if (template) {
        setFormRecords({
          name: template.name,
          type: template.type,
          id_positions: template.positions.map((item) => item.position_id),
          id_executors: template.positions.map((item) => item.position_id),
          id_locations: template.locations.map((item) => item.location_id),
          todo_items: template.todo_items.map((item) => item.title),
        });
      } else {
        notify("Шаблон не найден", "danger");
        navigate("/templates");
      }
    }
  }, [templateId, templates]);

  const [errors, setErrors] = useState<FormErrors>({});

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const templateFields = [
    {
      id: "name",
      title: "Название шаблона",
      type: "text",
      placeholder: "Название шаблона",
      options: undefined,
      isMulti: undefined,
    },
    {
      id: "type",
      title: "Тип модуля",
      type: "select",
      placeholder: "Выберите тип модуля",
      options: TYPE_OPTIONS,
      isMulti: false,
    },
    {
      id: "id_locations",
      title: "Филиалы",
      type: "select",
      placeholder: "Выберите филиалы для шаблона",
      options: locations
        .filter((loc) => loc.status)
        .map((loc) => ({
          value: loc.location_id,
          label: formRecords.id_locations.includes(loc.location_id)
            ? `${formRecords.id_locations.indexOf(loc.location_id) + 1}. ${loc.location}`
            : loc.location,
        })),
      isMulti: true,
    },
    {
      id: "id_positions",
      title: "Должности утверждающих пользователей",
      type: "select",
      placeholder: "Выберите должности по порядку утверждения",
      options: positions
        .filter((pos) => pos.status && pos.position.toLocaleLowerCase().trim() !== "создатель")
        .map((pos) => ({
          value: pos.position_id,
          label: formRecords.id_positions.includes(pos.position_id)
            ? `${formRecords.id_positions.indexOf(pos.position_id) + 1}. ${pos.position}`
            : pos.position,
        })),
      isMulti: true,
    },
    {
      id: "id_executors",
      title: "Должности исполняющих пользователей",
      type: "select",
      placeholder: "Выберите должности по порядку исполнения",
      options: positions
        .filter((pos) => pos.status)
        .map((pos) => ({
          value: pos.position_id,
          label: formRecords.id_executors.includes(pos.position_id)
            ? `${formRecords.id_executors.indexOf(pos.position_id) + 1}. ${pos.position}`
            : pos.position,
        })),
      isMulti: true,
    },
    {
      id: "todo_items",
      title: "Чек-лист к исполнению",
      type: "input",
      placeholder: "Введите наименование для проверки при исполнении",
      options: undefined,
      isMulti: true,
    },
  ] as const;

  const validateForm = () => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      const value = formRecords[field];
      if (Array.isArray(value) ? value.length === 0 : !value?.toString().trim()) {
        newErrors[field] = true;
      }
    });

    // Validate todo_items
    if (isCreatorAnExecutor) {
      const items = formRecords.todo_items as string[];
      if (!items.length) {
        newErrors["todo_items"] = true;
      } else {
        items.forEach((item, idx) => {
          if (!item.trim()) {
            newErrors[`todo_items_${idx}`] = true;
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
  type SelectChangeEvent = readonly SelectOption[] | null;
  type SingleSelectEvent = SelectOption | null;

  const handleInputChange = (e: InputChangeEvent | SelectChangeEvent | SingleSelectEvent, fieldId?: string) => {
    if (e && "target" in e) {
      // Handle input change
      const { name, value } = e.target;
      // Special handling for todo_items (handled separately)
      if (name.startsWith("todo_item_")) {
        const idx = Number(name.replace("todo_item_", ""));
        setFormRecords((prev) => {
          const newItems = [...prev.todo_items];
          newItems[idx] = value.slice(0, 100);
          return { ...prev, todo_items: newItems };
        });
        setErrors((prev) => {
          const { [`todo_items_${idx}`]: _, ...rest } = prev;
          return rest;
        });
      } else {
        setFormRecords((prev) => ({ ...prev, [name]: value.slice(0, 100) }));
        setErrors((prev) => ({ ...prev, [name]: false }));
      }
    } else {
      // Handle select change
      // @ts-ignore
      const values = Array.isArray(e) ? e.map((option) => option.value) : e ? [e.value] : [];
      if (fieldId === "id_executors") {
        setFormRecords((prev) => ({
          ...prev,
          [fieldId]: values,
          todo_items: isCreatorAnExecutor ? prev.todo_items : [],
        }));
        setErrors((prev) => ({ ...prev, [fieldId]: false, todo_items: false }));
      } else {
        setFormRecords((prev) => ({ ...prev, [fieldId!]: values }));
        setErrors((prev) => ({ ...prev, [fieldId!]: false }));
      }
    }
  };

  const submitTemplate = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      notify("Заполните все поля!", "danger");
      return;
    }

    setFormLoading(true);
    try {
      await addTemplate({
        ...formRecords,
        type: TYPE_OPTIONS.find((type) => type.value === +formRecords.type)?.label,
      });
      notify("Шаблон создан", "success");

      setFormSubmitted(true);
      getTemplatesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", templateId ? "Ошибка при обновлении шаблона" : "Ошибка при создании шаблона");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, type, placeholder, options, isMulti }: (typeof templateFields)[number]) => {
    if (id === "todo_items") {
      if (!isCreatorAnExecutor) return null;
      return (
        <div className={"col-lg-12"} key={id}>
          <div className="mb-3">
            <label className="form-label">{title}</label>
            {formRecords.todo_items.map((item, idx) => (
              <div className="input-group mb-2" key={idx}>
                <input
                  type="text"
                  className={`form-control ${errors[`todo_items_${idx}`] ? "is-invalid" : ""}`}
                  name={`todo_item_${idx}`}
                  placeholder={placeholder}
                  value={item}
                  maxLength={100}
                  onChange={handleInputChange}
                  disabled={isFormLoading}
                />
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() =>
                    setFormRecords((prev) => {
                      const newItems = [...prev.todo_items];
                      newItems.splice(idx, 1);
                      return { ...prev, todo_items: newItems };
                    })
                  }
                  disabled={isFormLoading || formRecords.todo_items.length === 1}
                  tabIndex={-1}
                  title="Удалить пункт"
                >
                  -
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline-success btn-sm mb-2"
              onClick={() =>
                setFormRecords((prev) => {
                  const newItems = [...prev.todo_items];
                  newItems.splice(newItems.length, 0, "");
                  return { ...prev, todo_items: newItems };
                })
              }
            >
              + Добавить пункт
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className={id === "id_positions" || id === "id_executors" ? "col-lg-6" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label className="form-label required">{title}</label>
          <div className="input-icon mb-3">
            {type === "text" ? (
              <input
                type="text"
                className={`form-control rounded-2xl ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                placeholder={placeholder}
                value={(formRecords[id] as string) || ""}
                onChange={handleInputChange}
                disabled={isFormLoading}
              />
            ) : (
              <Select<SelectOption, typeof isMulti>
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={
                  isMulti
                    ? (formRecords[id] as number[])
                        .map((value) => options?.find((opt) => opt.value === value))
                        .filter((opt): opt is SelectOption => opt !== undefined)
                    : options?.find((opt) => opt.value === Number(formRecords[id]))
                }
                onChange={(newValue) => handleInputChange(newValue, id)}
                isDisabled={isFormLoading}
                options={options}
                isMulti={isMulti}
                isSearchable
                closeMenuOnSelect={id === "type" ? true : false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            )}

            <div className="invalid-feedback">{getInputErrorMessage(id)}</div>
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newTemplateBody = (
    <form id="templateForm" className="row" onSubmit={submitTemplate}>
      {templateFields.map(renderField)}
    </form>
  );

  const newTemplateFooter = (
    <button type="submit" form="templateForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
      {templateId ? "Сохранить изменения" : "Создать шаблон"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header={templateId ? "Редактирование шаблона" : "Новый шаблон"}
      body={newTemplateBody}
      footer={newTemplateFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
